import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Modal, Button, Alert } from 'react-bootstrap';
import { STORAGE_KEYS, BASE_PATH } from '../utils/constants';

// Decode base64url (JWT uses base64url, not standard base64)
const base64UrlDecode = (str) => {
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  if (pad) base64 += '='.repeat(4 - pad);
  return atob(base64);
};

const WARNING_TIME_MS = 5 * 60 * 1000; // Show warning 5 minutes before expiry

const SessionManager = () => {
  const { user, sessionExpired, refreshSession, logout } = useAuth();
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [extending, setExtending] = useState(false);
  const countdownRef = useRef(null);
  const checkIntervalRef = useRef(null);

  // Show expired modal when sessionExpired flag is set
  useEffect(() => {
    if (sessionExpired) {
      setSessionWarning(false);
      clearCountdown();
      setShowSessionModal(true);
    }
  }, [sessionExpired]);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const getTimeUntilExpiry = useCallback(() => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) return -1;

    try {
      const tokenPayload = JSON.parse(base64UrlDecode(token.split('.')[1]));
      if (typeof tokenPayload.exp !== 'number') return Infinity;
      return tokenPayload.exp * 1000 - Date.now();
    } catch {
      return -1;
    }
  }, []);

  // Start countdown timer
  const startCountdown = useCallback(
    (initialSeconds) => {
      clearCountdown();
      setTimeLeft(initialSeconds);
      setSessionWarning(true);

      countdownRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearCountdown();
            setSessionWarning(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [clearCountdown]
  );

  // Track sessionWarning via ref to avoid it as a useEffect dependency
  const sessionWarningRef = useRef(sessionWarning);
  sessionWarningRef.current = sessionWarning;

  // Periodic check for session expiry warning
  useEffect(() => {
    if (!user) return;

    const checkSessionWarning = () => {
      const timeUntilExpiry = getTimeUntilExpiry();

      if (timeUntilExpiry <= 0) {
        // Already expired — modal will be shown via sessionExpired flag
        clearCountdown();
        setSessionWarning(false);
        return;
      }

      if (timeUntilExpiry <= WARNING_TIME_MS && !sessionWarningRef.current) {
        startCountdown(Math.ceil(timeUntilExpiry / 1000));
      }
    };

    checkSessionWarning(); // Initial check
    checkIntervalRef.current = setInterval(checkSessionWarning, 30000); // Check every 30s

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      clearCountdown();
    };
  }, [user, getTimeUntilExpiry, startCountdown, clearCountdown]);

  const handleExtendSession = async () => {
    if (extending) return;
    setExtending(true);
    try {
      const success = await refreshSession();
      if (success) {
        clearCountdown();
        setSessionWarning(false);
        setShowSessionModal(false);
      } else {
        await logout();
      }
    } catch (error) {
      console.error('Session extension error:', error);
      await logout();
    } finally {
      setExtending(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (!user) return null;

  return (
    <>
      {/* Session Warning */}
      {sessionWarning && (
        <Alert
          variant="warning"
          className="position-fixed top-0 start-50 translate-middle-x mt-3"
          style={{ zIndex: 9999, minWidth: '400px' }}
        >
          <Alert.Heading>Session Expiring Soon</Alert.Heading>
          <p>Your session will expire in {formatTime(timeLeft)}. Click "Extend Session" to continue working.</p>
          <hr />
          <div className="d-flex justify-content-end gap-2">
            <Button variant="outline-warning" size="sm" onClick={handleLogout}>
              Logout
            </Button>
            <Button variant="warning" size="sm" onClick={handleExtendSession} disabled={extending}>
              {extending ? 'Extending...' : 'Extend Session'}
            </Button>
          </div>
        </Alert>
      )}

      {/* Session Expired Modal */}
      <Modal show={showSessionModal} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>Session Expired</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Your session has expired. Please login again to continue.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => (window.location.href = `${BASE_PATH}/login`)}>
            Go to Login
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SessionManager;
